import _defineProperty from "/app/node_modules/.pnpm/next@12.3.3_4afada721a4a39e35a1a66028f2134fd/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/defineProperty.js";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import { InputText, Button, Cell, Spacer, Text } from '@vinted/web-ui';
import useTranslate from 'hooks/useTranslate';
import useGoogleTagManagerTrack from 'hooks/useGoogleTagManagerTrack';
import { AuthenticateGrantType } from 'constants/auth';
import { GoogleTagManagerEvent } from 'constants/google';
import { CODE_LOGIN, FORGOT_PASSWORD_URL, SIGNUP_HELP_ENTRIES_URL } from 'constants/routes';
import { getCaptchaVerification, getCurrentUser, verifyCaptcha } from 'data/api';
import { authenticateUser } from 'data/api/account';
import { ResponseCode } from 'data/api/response-codes';
import { getIsFeatureSwitchEnabled } from 'state/feature-switches/selectors';
import { getFingerprint } from 'libs/utils/fingerprint';
import { toParams } from 'libs/utils/url';
import { getUrlQuery, navigateToPage, reloadPage } from 'libs/utils/window';
import useDataDomeCaptcha from 'hooks/useDatadomeCaptcha';
import { actions } from 'state/auth/slice';
import { AuthView } from 'state/auth/constants';
import Captcha from 'components/Captcha';
import ContentLoader from 'components/ContentLoader';
import PasswordField from '../PasswordField';
import useSuccessUrl from '../hooks/useSuccessUrl';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";

const EmailLogin = () => {
  const {
    login
  } = toParams(getUrlQuery());
  const username = typeof login === 'string' ? login : undefined;
  const isUuidValidationEnabled = useSelector(getIsFeatureSwitchEnabled('user_captcha_uuid_validate'));
  const {
    0: isLoading,
    1: setIsLoading
  } = useState(false);
  const dispatch = useDispatch();
  const translate = useTranslate('auth.email.login');
  const {
    register,
    handleSubmit
  } = useForm({
    defaultValues: {
      username
    }
  });
  const {
    googleTagManagerTrack
  } = useGoogleTagManagerTrack();
  const {
    0: error,
    1: setError
  } = useState('');
  const {
    0: isCaptchaVisible,
    1: setIsCaptchaVisible
  } = useState(false);
  const {
    0: isCaptchaResolved,
    1: setIsCaptchaResolved
  } = useState(false);
  const {
    0: uuid,
    1: setUuid
  } = useState('');
  const {
    0: formData,
    1: setFormData
  } = useState();
  const successUrl = useSuccessUrl();

  function handleForgotPasswordClick(event) {
    event.preventDefault();
    dispatch(actions.setAuthView({
      authView: AuthView.ResetPassword
    }));
  }

  async function performLogin(data, captchaUuid) {
    const fingerprint = await getFingerprint();

    const authenticateUserArgs = _objectSpread({
      grantType: AuthenticateGrantType.Password,
      fingerprint
    }, data);

    if (isUuidValidationEnabled) {
      authenticateUserArgs.uuid = captchaUuid;
    }

    const response = await authenticateUser(authenticateUserArgs);

    if (response.code === ResponseCode.Required2FA && 'payload' in response && response.payload) {
      navigateToPage(CODE_LOGIN({
        refUrl: successUrl,
        authProps: response.payload
      }));
      return;
    }

    if ('errors' in response) {
      // TODO: handle ResponseCode.SessionFromTokenError
      setError(response.message);
      setIsCaptchaVisible(false);
      setIsCaptchaResolved(false);
      setIsLoading(false);
      return;
    }

    const userResp = await getCurrentUser();
    const user = 'errors' in userResp ? null : userResp.user;
    googleTagManagerTrack(GoogleTagManagerEvent.Login, {
      auth_type: 'email',
      user_email: user === null || user === void 0 ? void 0 : user.email
    });
    navigateToPage(successUrl);
  }

  async function handleFormSubmit(data) {
    setIsLoading(true);
    setError('');
    setFormData(data);
    const captchaResponse = await getCaptchaVerification({
      entityType: 'login',
      payload: {
        username: data.username
      }
    });
    if ('errors' in captchaResponse) return;

    if (!captchaResponse.verified) {
      setUuid(captchaResponse.uuid);
      setIsCaptchaVisible(true);
      return;
    }

    performLogin(data, captchaResponse.uuid);
  }

  useDataDomeCaptcha(() => {
    if (!formData) return;
    handleFormSubmit(formData);
  });

  async function handleCaptchaComplete(key) {
    if (!formData) return;
    setIsCaptchaResolved(true);
    const verifyResponse = await verifyCaptcha({
      uuid,
      captchaToken: key
    });

    if ('errors' in verifyResponse || !verifyResponse.verified) {
      reloadPage();
      return;
    }

    performLogin(formData, verifyResponse.uuid);
  }

  function renderDescription() {
    return /*#__PURE__*/_jsxs("div", {
      className: "u-ui-margin-horizontal-large",
      children: [/*#__PURE__*/_jsx(Text, {
        id: "auth_modal_title",
        text: translate('title'),
        type: Text.Type.Heading,
        width: Text.Width.Parent,
        alignment: Text.Alignment.Center
      }), /*#__PURE__*/_jsx(Spacer, {
        size: Spacer.Size.Large
      }), !!error && /*#__PURE__*/_jsx(Text, {
        text: error,
        theme: "warning",
        width: Text.Width.Parent,
        alignment: Text.Alignment.Center
      })]
    });
  }

  function renderFields() {
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(InputText, _objectSpread(_objectSpread({}, register('username', {
        required: true
      })), {}, {
        placeholder: translate('fields.login'),
        uncontrolled: true,
        disabled: !!username
      })), !!username && /*#__PURE__*/_jsx("input", _objectSpread(_objectSpread({
        type: "hidden"
      }, register('username')), {}, {
        value: username
      })), /*#__PURE__*/_jsx(PasswordField, _objectSpread(_objectSpread({}, register('password', {
        required: true
      })), {}, {
        placeholder: translate('fields.password'),
        uncontrolled: true
      }))]
    });
  }

  function renderActions() {
    return /*#__PURE__*/_jsxs(Cell, {
      children: [/*#__PURE__*/_jsx(Button, {
        text: translate('actions.submit'),
        type: Button.Type.Submit,
        styling: Button.Styling.Filled,
        disabled: isLoading,
        isLoading: isLoading
      }), /*#__PURE__*/_jsx(Spacer, {
        size: Spacer.Size.XLarge
      }), /*#__PURE__*/_jsx(Text, {
        width: Text.Width.Parent,
        alignment: Text.Alignment.Center,
        children: /*#__PURE__*/_jsx("a", {
          href: FORGOT_PASSWORD_URL,
          onClick: handleForgotPasswordClick,
          children: translate('actions.forgot_password')
        })
      }), /*#__PURE__*/_jsx(Spacer, {
        size: Spacer.Size.Medium
      }), /*#__PURE__*/_jsx(Text, {
        width: Text.Width.Parent,
        alignment: Text.Alignment.Center,
        children: /*#__PURE__*/_jsx("a", {
          href: SIGNUP_HELP_ENTRIES_URL,
          children: translate('actions.help')
        })
      }), /*#__PURE__*/_jsx(Spacer, {})]
    });
  }

  if (isCaptchaVisible) {
    return /*#__PURE__*/_jsx("div", {
      className: "u-ui-padding-large",
      children: isCaptchaResolved ? /*#__PURE__*/_jsx("div", {
        className: "u-ui-padding-bottom-x2-large",
        children: /*#__PURE__*/_jsx(ContentLoader, {
          styling: ContentLoader.Styling.Wide
        })
      }) : /*#__PURE__*/_jsx(Captcha, {
        onComplete: handleCaptchaComplete
      })
    });
  }

  return /*#__PURE__*/_jsxs("form", {
    onSubmit: handleSubmit(handleFormSubmit),
    children: [renderDescription(), renderFields(), /*#__PURE__*/_jsx(Spacer, {}), renderActions()]
  });
};

export default EmailLogin;